import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
    Plus, Trash2, CheckCircle, AlertTriangle, Sparkles, 
    Bed, User, DollarSign, Printer, CheckCircle2, Search, X 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import ExtrasPrint from './extrasprint';
import '../Dashboard.css';

// Utilitaire pour extraire les tableaux de manière sécurisée
const toArray = (response, keys = []) => {
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    for (const key of keys) {
      if (Array.isArray(raw[key])) return raw[key];
    }
    if (Array.isArray(raw.data)) return raw.data;
  }
  return [];
};

const PosterExtras = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const company_id = user?.company_id || user?.companyId;

  // --- ÉTATS PRINCIPAUX ---
  const [servicesExtras, setServicesExtras] = useState([]);
  const [clientsEnChambre, setClientsEnChambre] = useState([]);
  const [customersList, setCustomersList] = useState([]); 
  const [companyInfo, setCompanyInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- TYPE DE CLIENT ('CHAMBRE' ou 'EXTERIEUR') ---
  const [typeClient, setTypeClient] = useState('CHAMBRE'); 
  const [selectedLigneChambre, setSelectedLigneChambre] = useState(null);
  
  // --- CLIENT EXTÉRIEUR / DIVERS (Recherche & Sélection) ---
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [nomClientExt, setNomClientExt] = useState(''); 

  // --- PANIER DES PRESTATIONS EXTRAS SÉLECTIONNÉES ---
  const [panierExtras, setPanierExtras] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [quantite, setQuantite] = useState(1);

  // --- PAIEMENT (POUR CLIENT EXTÉRIEUR) ---
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [montantRecu, setMontantRecu] = useState('');
  const [referencePaiement, setReferencePaiement] = useState('');

  // --- DONNÉES POUR L'IMPRESSION ---
  const [printData, setPrintData] = useState(null);
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Reçu_Prestation_${Date.now()}`,
  });

  // --- TOASTS ---
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  }, []);

  // --- CHARGEMENT DES DONNÉES ---
  const fetchData = async () => {
    try {
      setLoading(true);
      const [resExtras, resReservations, resMethods, resCustomers, compRes] = await Promise.all([
        API.get('/hebergement/extras').catch(() => ({ data: [] })),
        API.get('/hebergement/reservations').catch(() => ({ data: [] })),
        API.get('/plan-comptable/paiements/methodes').catch(() => ({ data: { success: false, data: [] } })),
        API.get('/customers').catch(() => ({ data: [] })),
        company_id ? API.get(`/company/${company_id}`).catch(() => ({ data: {} })) : Promise.resolve({ data: {} })
      ]);

      if (compRes.data?.success && compRes.data.data) {
        setCompanyInfo(compRes.data.data);
      }

      const rawExtras = resExtras.data;
      setServicesExtras(Array.isArray(rawExtras) ? rawExtras : (rawExtras?.data || rawExtras?.items || []));

      // Clients en chambre actifs
      const allReservations = toArray(resReservations, ['reservations', 'data']);
      const clientsActifs = [];
      allReservations.forEach(r => {
        if (!r) return;
        const statutRes = (r.statut || '').toUpperCase();
        if (r.chambres && Array.isArray(r.chambres) && r.chambres.length > 0) {
          const chambresActives = r.chambres.filter(ch => {
            const stCh = (ch?.statut || '').toUpperCase();
            return stCh === 'CHECK_IN' || stCh === 'EN_COURS';
          });
          if (chambresActives.length > 0) {
            clientsActifs.push({ ...r, chambres: chambresActives });
          }
        } else if (statutRes.includes('CHECK_IN') || statutRes.includes('EN_COURS')) {
          clientsActifs.push(r);
        }
      });
      setClientsEnChambre(clientsActifs);

      // Liste des clients de la base
      const rawCust = resCustomers.data;
      setCustomersList(Array.isArray(rawCust) ? rawCust : (rawCust?.data || []));

      // Méthodes de paiement POS
      if (resMethods.data && resMethods.data.success) {
        const actives = resMethods.data.data.filter(m => m.is_active === 1);
        setPaymentMethods(actives);
        if (actives.length > 0) {
          setSelectedPaymentMethodId(actives[0].id);
        }
      }
    } catch (err) {
      console.error("Erreur chargement:", err);
      showToast("Erreur de chargement des données", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [company_id]);

  // Filtrer les clients pour la recherche
  const filteredCustomers = customerSearchQuery.trim() && !selectedCustomer
    ? customersList.filter(c => {
        const name = (c.name || c.nom || '').toLowerCase();
        const phone = (c.phone || c.telephone || '').toLowerCase();
        const query = customerSearchQuery.toLowerCase();
        return name.includes(query) || phone.includes(query);
      }).slice(0, 10)
    : [];

  const selectedMethodObj = paymentMethods.find(m => m.id === selectedPaymentMethodId);
  const isNotCashPayment = selectedMethodObj && 
    !['wallet', 'especes', 'cash', 'esp'].includes((selectedMethodObj.icone_name || '').toLowerCase()) &&
    !['esp', 'espece', 'espèces'].includes((selectedMethodObj.libelle || '').toLowerCase());

  // --- GESTION DU PANIER ---
  const handleAddExtraToCart = () => {
    if (!selectedService) {
      showToast("Veuillez sélectionner un service extra", "error");
      return;
    }
    const s = servicesExtras.find(item => item.id === selectedService);
    if (!s) return;

    const qteNum = parseFloat(quantite) || 1;
    if (qteNum <= 0) {
      showToast("La quantité doit être supérieure à 0", "error");
      return;
    }

    const existingIndex = panierExtras.findIndex(item => item.service_extra_id === s.id);
    if (existingIndex > -1) {
      const copy = [...panierExtras];
      copy[existingIndex].quantite += qteNum;
      copy[existingIndex].montant_ttc = copy[existingIndex].quantite * copy[existingIndex].prix_unitaire;
      setPanierExtras(copy);
    } else {
      const prix = Number(s.prix_unitaire || 0);
      const newItem = {
        service_extra_id: s.id,
        composant_id: s.composant_id,
        code: s.code,
        nom: s.libelle,
        quantite: qteNum,
        prix_unitaire: prix,
        soumis_tva: s.soumis_tva,
        taux_tva: s.taux_tva,
        montant_ttc: qteNum * prix
      };
      setPanierExtras([...panierExtras, newItem]);
    }
    setSelectedService('');
    setQuantite(1);
  };

  const handleRemoveFromCart = (index) => {
    const copy = [...panierExtras];
    copy.splice(index, 1);
    setPanierExtras(copy);
  };

  const totalPanierTTC = panierExtras.reduce((sum, item) => sum + item.montant_ttc, 0);

  // --- VALIDATION DE LA VENTE (SIMPLE OU AVEC IMPRESSION) ---
  const executeValidation = async () => {
    if (panierExtras.length === 0) {
      showToast("Le panier des services extras est vide", "error");
      return null;
    }

    if (typeClient === 'CHAMBRE' && !selectedLigneChambre) {
      showToast("Veuillez sélectionner une chambre / un client en séjour", "error");
      return null;
    }

    if (typeClient === 'EXTERIEUR') {
      if (!selectedCustomer) {
        showToast("Veuillez sélectionner un client de référence dans la liste (ex: Client Divers)", "error");
        return null;
      }
      if (!nomClientExt.trim()) {
        showToast("Veuillez saisir le nom réel du visiteur dans le champ d'observation", "error");
        return null;
      }
      if (isNotCashPayment && !referencePaiement.trim()) {
        showToast("Veuillez saisir la référence de paiement (obligatoire pour ce mode)", "error");
        return null;
      }

      // --- CONTRÔLE STRICT DU MONTANT REÇU ---
      const montantVersement = parseFloat(montantRecu);
      if (isNaN(montantVersement) || montantVersement < 0) {
        showToast("Veuillez saisir un montant reçu valide (compris entre 0 et plus).", "error");
        return null;
      }
    }

    const montantVersement = parseFloat(montantRecu) || 0;
    const detailsVente = {
      client_nom: typeClient === 'EXTERIEUR' ? nomClientExt.trim() : (selectedLigneChambre?.nom || 'Client Chambre'),
      mode_reglement: selectedMethodObj ? selectedMethodObj.libelle : 'ESPECES',
      montant_total: totalPanierTTC,
      montant_recu: montantVersement,
      items: [...panierExtras]
    };

    setIsSubmitting(true);
    try {
      for (const item of panierExtras) {
        const payload = {
          groupe_id: typeClient === 'CHAMBRE' ? selectedLigneChambre?.groupe_id : null,
          ligne_chambre_id: typeClient === 'CHAMBRE' ? selectedLigneChambre?.ligne_chambre_id : null,
          customer_id: typeClient === 'EXTERIEUR' ? (selectedCustomer.id || selectedCustomer._id) : (selectedLigneChambre?.customer_id || null),
          service_extra_id: item.service_extra_id,
          quantite: item.quantite,
          moyen_paiement: selectedMethodObj ? selectedMethodObj.libelle : 'ESPECES',
          payment_method_id: selectedPaymentMethodId || null,
          reference: referencePaiement.trim() || null,
          montant_verse: typeClient === 'EXTERIEUR' ? montantVersement : 0,
          observation: typeClient === 'EXTERIEUR' 
            ? `Visiteur : ${nomClientExt.trim()}` 
            : `Prestation résidente`
        };
        await API.post('/hebergement/extras/consommations', payload);
      }

      showToast(typeClient === 'CHAMBRE' ? "Prestations postées sur la facture de la chambre !" : "Vente extra enregistrée avec succès !");
      
      // Reset formulaire
      setPanierExtras([]);
      setSelectedLigneChambre(null);
      setSelectedCustomer(null);
      setCustomerSearchQuery('');
      setNomClientExt('');
      setMontantRecu('');
      setReferencePaiement('');
      return detailsVente;
    } catch (err) {
      console.error("Erreur validation extra:", err);
      showToast(err.response?.data?.error || "Erreur lors de l'enregistrement", "error");
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bouton 1 : Validation Simple
  const handleValidationSimple = async () => {
    await executeValidation();
  };

  // Bouton 2 : Validation & Impression Directe
  const handleValidationEtImprimer = async () => {
    const res = await executeValidation();
    if (res) {
      setPrintData(res);
      setTimeout(() => {
        handlePrint();
      }, 400);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, overflowY: 'auto', background: '#f8fafc', position: 'relative' }}>
        
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px', color: '#1e1b4b' }}>
              <Sparkles size={22} color="#7c3aed" /> GUICHET SERVICES EXTRAS (POS & CHAMBRE)
            </h1>
            <div style={{ color: '#64748b', fontSize: '13px' }}>
              Vente directe (Recherche client de référence + nom réel) ou imputation sur séjour hôtelier
            </div>
          </div>
        </header>

        <div style={{ padding: '30px 40px', display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '25px' }}>
          
          {/* COLONNE GAUCHE */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* 1. CHOIX DU TYPE DE CLIENT */}
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#f5f3ff', fontWeight: '900', fontSize: '13px', color: '#5b21b6' }}>
                1. IDENTIFICATION DU BÉNÉFICIAIRE
              </div>
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                  <button 
                    type="button"
                    onClick={() => { setTypeClient('CHAMBRE'); setSelectedCustomer(null); setCustomerSearchQuery(''); setNomClientExt(''); }}
                    style={{ flex: 1, padding: '12px', borderRadius: '8px', border: typeClient === 'CHAMBRE' ? '2px solid #7c3aed' : '1px solid #cbd5e1', background: typeClient === 'CHAMBRE' ? '#f5f3ff' : '#fff', fontWeight: '800', color: typeClient === 'CHAMBRE' ? '#7c3aed' : '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    <Bed size={16} /> Client en Chambre 
                  </button>
                  <button 
                    type="button"
                    onClick={() => { setTypeClient('EXTERIEUR'); setSelectedLigneChambre(null); }}
                    style={{ flex: 1, padding: '12px', borderRadius: '8px', border: typeClient === 'EXTERIEUR' ? '2px solid #7c3aed' : '1px solid #cbd5e1', background: typeClient === 'EXTERIEUR' ? '#f5f3ff' : '#fff', fontWeight: '800', color: typeClient === 'EXTERIEUR' ? '#7c3aed' : '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    <User size={16} /> Client Extérieur / Divers 
                  </button>
                </div>

                {typeClient === 'CHAMBRE' ? (
                  <div>
                    <label style={labelStyle}>SÉLECTIONNER LA CHAMBRE / LE SÉJOUR *</label>
                    <select 
                      style={inputStyle}
                      value={selectedLigneChambre ? JSON.stringify(selectedLigneChambre) : ''}
                      onChange={(e) => setSelectedLigneChambre(e.target.value ? JSON.parse(e.target.value) : null)}
                    >
                      <option value="">-- Choisir un client en chambre --</option>
                      {clientsEnChambre.map((res) => {
                        const chambresList = res.chambres || [];
                        return chambresList.map((ch, idx) => {
                          const ligneId = ch.id || ch.ligne_id || `ch-${idx}`;
                          const numCh = ch.chambre_numero || ch.numero || '---';
                          const nomCli = ch.client_nom || res.client_nom || res.reservant_nom || 'Client';
                          const custId = ch.customer_id || res.customer_id || null; 
                          
                          const objVal = { 
                            groupe_id: res.id, 
                            ligne_chambre_id: ligneId, 
                            customer_id: custId, 
                            nom: nomCli, 
                            chambre: numCh 
                          };
                          
                          return (
                            <option key={`${res.id}-${ligneId}`} value={JSON.stringify(objVal)}>
                              Chambre N° {numCh} — {nomCli} (Séjour #{res.id})
                            </option>
                          );
                        });
                      })}
                    </select>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    
                    {/* CHAMP DE RECHERCHE CLIENT INTERACTIF */}
                    <div>
                      <label style={labelStyle}>RECHERCHER UN CLIENT DE RÉFÉRENCE (Ex: Client Divers) *</label>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                        <Search size={16} style={{ position: 'absolute', left: '12px', color: '#64748b' }} />
                        <input 
                          type="text"
                          style={{ ...inputStyle, marginTop: 0, paddingLeft: '38px', paddingRight: selectedCustomer ? '30px' : '14px' }}
                          placeholder="Tapez le nom ou téléphone du client..."
                          value={customerSearchQuery}
                          onChange={(e) => {
                            setCustomerSearchQuery(e.target.value);
                            if (selectedCustomer) setSelectedCustomer(null);
                          }}
                        />
                        {selectedCustomer && (
                          <button 
                            type="button"
                            onClick={() => { setSelectedCustomer(null); setCustomerSearchQuery(''); }}
                            style={{ position: 'absolute', right: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444' }}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                      {filteredCustomers.length > 0 && (
                        <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', marginTop: '5px', maxHeight: '220px', overflowY: 'auto', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                          {filteredCustomers.map((cust) => {
                            const custName = cust.name || cust.nom || '';
                            const custPhone = cust.phone || cust.telephone || 'Sans tel';
                            return (
                              <div 
                                key={cust.id || cust._id}
                                onClick={() => {
                                  setSelectedCustomer(cust);
                                  setCustomerSearchQuery(`${custName} (${custPhone})`);
                                }}
                                style={{ padding: '10px 14px', fontSize: '12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f3ff'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                              >
                                <span style={{ fontWeight: '700', color: '#1e293b' }}>{custName}</span>
                                <span style={{ color: '#64748b', fontSize: '11px' }}>{custPhone}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {selectedCustomer && (
                      <div style={{ background: '#f5f3ff', padding: '15px', borderRadius: '8px', border: '1px dashed #7c3aed' }}>
                        <label style={{ ...labelStyle, color: '#5b21b6' }}>NOM / IDENTITÉ DU VISITEUR (OBSERVATION) *</label>
                        <input 
                          type="text" 
                          style={inputStyle} 
                          placeholder="Entrez le nom du visiteur (ex: M. Kouassi)..."
                          value={nomClientExt}
                          onChange={(e) => setNomClientExt(e.target.value)}
                        />
                        <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                          Ce nom sera inscrit dans l'observation pour identifier le visiteur.
                        </span>
                      </div>
                    )}

                  </div>
                )}
              </div>
            </div>

            {/* 2. CATALOGUE DES SERVICES EXTRAS */}
            <div style={contentCardStyle}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#f5f3ff', fontWeight: '900', fontSize: '13px', color: '#5b21b6' }}>
                2. CATALOGUE DES PRESTATIONS EXTRAS
              </div>
              <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={labelStyle}>SERVICE EXTRA</label>
                  <select 
                    style={inputStyle}
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                  >
                    <option value="">-- Sélectionner une prestation --</option>
                    {servicesExtras.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.libelle} ({item.code}) — {Number(item.prix_unitaire || 0).toLocaleString()} F
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>QUANTITÉ</label>
                    <input 
                      type="number" 
                      style={inputStyle} 
                      min="1" 
                      value={quantite}
                      onChange={(e) => setQuantite(e.target.value)}
                    />
                  </div>
                  <button 
                    type="button" 
                    onClick={handleAddExtraToCart}
                    style={btnPrimaryStyle}
                  >
                    <Plus size={16} /> Ajouter au Panier
                  </button>
                </div>
              </div>
            </div>

          </div>

          {/* COLONNE DROITE : PANIER & VALIDATION */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            <div style={contentCardStyle}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#f5f3ff', fontWeight: '900', fontSize: '13px', color: '#5b21b6', display: 'flex', justifyContent: 'space-between' }}>
                <span>3. RÉCAPITULATIF DU PANIER</span>
                <span>{panierExtras.length} article(s)</span>
              </div>
              <div style={{ padding: '20px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#64748b', fontSize: '11px', textAlign: 'left' }}>
                      <th style={{ padding: '8px' }}>Prestation</th>
                      <th style={{ padding: '8px', textAlign: 'center' }}>Qté</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Total TTC</th>
                      <th style={{ padding: '8px', width: '30px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {panierExtras.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center', padding: '30px', color: '#64748b', fontSize: '12px' }}>
                          Aucun service ajouté au panier.
                        </td>
                      </tr>
                    ) : (
                      panierExtras.map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '12px' }}>
                          <td style={{ padding: '10px 8px', fontWeight: '700', color: '#1e293b' }}>{item.nom}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'center' }}>{item.quantite}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '800', color: '#7c3aed' }}>
                            {Math.round(item.montant_ttc).toLocaleString()} F
                          </td>
                          <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                            <button 
                              onClick={() => handleRemoveFromCart(idx)} 
                              style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '4px', cursor: 'pointer' }}
                            >
                              <Trash2 size={12}/>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* BLOC TOTAL & PAIEMENT */}
                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: '900', color: '#1e293b', marginBottom: typeClient === 'EXTERIEUR' ? '12px' : '0' }}>
                    <span>{typeClient === 'CHAMBRE' ? 'MONTANT TOTAL IMPUTÉ :' : 'TOTAL À PAYER :'}</span>
                    <span style={{ color: '#7c3aed' }}>{Math.round(totalPanierTTC).toLocaleString()} F</span>
                  </div>

                  {typeClient === 'CHAMBRE' ? (
                    <div style={{ marginTop: '8px', fontSize: '11px', fontWeight: '700', color: '#0284c7', background: '#e0f2fe', padding: '8px', borderRadius: '6px' }}>
                      ℹ️ Ce montant sera automatiquement ajouté à la note de la chambre.
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b' }}>MODE DE RÈGLEMENT</label>
                        <select 
                          style={{ ...inputStyle, padding: '8px' }}
                          value={selectedPaymentMethodId}
                          onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                        >
                          {paymentMethods.map(m => (
                            <option key={m.id} value={m.id}>{m.libelle}</option>
                          ))}
                        </select>
                      </div>

                      {isNotCashPayment && (
                        <div style={{ marginBottom: '10px', background: '#fef3c7', padding: '10px', borderRadius: '6px', border: '1px solid #f59e0b' }}>
                          <label style={{ fontSize: '10px', fontWeight: '900', color: '#b45309' }}>RÉFÉRENCE DE PAIEMENT / TRANSACTION *</label>
                          <input 
                            type="text" 
                            style={{ ...inputStyle, padding: '8px', background: '#fff', marginTop: '4px' }}
                            placeholder="Ex: N° de transaction, chèque..."
                            value={referencePaiement}
                            onChange={(e) => setReferencePaiement(e.target.value)}
                          />
                        </div>
                      )}

                      <div>
                        <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b' }}>MONTANT REÇU (F CFA) *</label>
                        <input 
                          type="number" 
                          min="0"
                          step="any"
                          style={{ ...inputStyle, padding: '8px', fontWeight: 'bold', fontSize: '14px' }}
                          placeholder="0"
                          value={montantRecu}
                          onChange={(e) => setMontantRecu(e.target.value)}
                        />
                      </div>
                      <div style={{ marginTop: '8px', fontSize: '11px', fontWeight: '800', color: '#059669' }}>
                        Monnaie à rendre : {Math.max(0, (parseFloat(montantRecu) || 0) - totalPanierTTC).toLocaleString()} F
                      </div>
                    </>
                  )}
                </div>

                {/* BOUTONS DE VALIDATION (SIMPLE OU AVEC IMPRESSION) */}
                {typeClient === 'CHAMBRE' ? (
                  <button 
                    type="button"
                    onClick={handleValidationSimple}
                    disabled={isSubmitting || panierExtras.length === 0}
                    style={{ width: '100%', padding: '14px', background: panierExtras.length > 0 ? '#7c3aed' : '#cbd5e1', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '900', cursor: panierExtras.length > 0 ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '14px' }}
                  >
                    <CheckCircle2 size={18} /> Poster sur la Facture Chambre
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {/* Bouton 1 : Validation Simple */}
                    <button 
                      type="button"
                      onClick={handleValidationSimple}
                      disabled={isSubmitting || panierExtras.length === 0}
                      style={{ flex: 1, padding: '12px', background: '#64748b', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '900', cursor: panierExtras.length > 0 ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px' }}
                    >
                      <CheckCircle2 size={16} /> Validation Simple
                    </button>
                    {/* Bouton 2 : Valider & Imprimer */}
                    <button 
                      type="button"
                      onClick={handleValidationEtImprimer}
                      disabled={isSubmitting || panierExtras.length === 0}
                      style={{ flex: 1, padding: '12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '900', cursor: panierExtras.length > 0 ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px' }}
                    >
                      <Printer size={16} /> Valider & Imprimer
                    </button>
                  </div>
                )}

              </div>
            </div>

          </div>

        </div>
      </main>

      {/* Composant masqué pour l'impression */}
      {printData && (
        <div style={{ position: 'absolute', top: '-9999px', left: '-9999px', width: '80mm', opacity: '0', pointerEvents: 'none' }}>
          <ExtrasPrint 
            ref={printRef} 
            venteData={printData} 
            company={companyInfo} 
          />
        </div>
      )}

      <style>{`@keyframes slideInToast { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
};

// --- STYLES ---
const headerStyle = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const contentCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', overflow: 'hidden' };
const btnPrimaryStyle = { background: '#7c3aed', color: 'white', border: 'none', padding: '11px 18px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '800', fontSize: '12px', whiteSpace: 'nowrap' };
const inputStyle = { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', marginTop: '4px', background: '#fff', boxSizing: 'border-box' };
const labelStyle = { fontSize: '11px', fontWeight: '900', color: '#475569', textTransform: 'uppercase' };
const toastContainerStyle = { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, color: 'white', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };

export default PosterExtras;